<template>
    <AdminLayout>
        <template #topbar-search>
            <div class="flex flex-column">
                <IconField>
                    <InputIcon class="pi pi-search" />
                    <InputText v-model="searchValue" placeholder="Search by keywords..." size="small" />
                </IconField>
            </div>
        </template>
        <template #header>
            <Breadcrumb :items="breadcrumbItems" />
            <Button label="Export" class="accent-button" @click="handleExport" />
        </template>

        <div class="container">
            <Form class="form">
                <div class="grid grid-nogutter gap-4 mb-4 p-4">
                    <div class="col-12 md:col-3 flex flex-column justify-content-start gap-1">
                        <span class="Label">Grade details</span>
                        <span class="Caption">View & add grade details</span>
                    </div>

                    <div class="col-12 md:col-6 flex flex-column gap-3">
                        <div class="flex align-items-center justify-content-between">
                            <label for="name" class="Label align-self-start">Grade name</label>
                            <span class="naming-guide-toggle" @click="showNamingGuide = !showNamingGuide">
                                <i :class="showNamingGuide ? 'pi pi-chevron-up' : 'pi pi-question-circle'"></i>
                                How to name a grade
                            </span>
                        </div>
                        <div class="flex flex-column gap-1 relative">
                            <InputText id="name" v-model="form.name" placeholder="e.g. Grade 10" size="small"
                                variant="filled" class="w-full" :class="{ 'p-invalid': formErrors.name }"
                                @input="onGradeNameInput" @blur="hideSuggestions" autocomplete="off" />

                            <div v-if="suggestedGrades.length > 0 && !editingGrade"
                                class="surface-overlay border-1 surface-border border-round mt-1 absolute z-5 w-full bg-white"
                                style="top: 100%; left: 0; max-height: 200px; overflow-y: auto; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
                                <ul class="list-none p-0 m-0">
                                    <li v-for="grade in suggestedGrades" :key="grade._id"
                                        class="flex align-items-center justify-content-between px-2 py-1 hover:surface-100 cursor-pointer border-bottom-1 surface-border"
                                        @mousedown.prevent="selectSuggestedGrade(grade)">
                                        <span class="text-sm font-medium">{{ grade.name }}</span>
                                    </li>
                                </ul>
                            </div>

                            <!-- Warnings -->
                            <small v-if="formErrors.name" class="p-error">{{ formErrors.name }}</small>
                            <small v-else-if="gradeNameWarning" class="p-warn">{{ gradeNameWarning }}</small>
                            <small v-else class="p-error">&nbsp;</small>

                            <div v-if="showNamingGuide" class="naming-guide">
                                <p class="naming-guide-lead">
                                    The grade name sets the <strong>commission rate charged on every session</strong> for
                                    that grade, so it has to contain something the billing rules recognise. Include any
                                    one of:
                                </p>
                                <ul class="naming-guide-list">
                                    <li><strong>A grade number</strong> — <code>Grade 05</code> to <code>Grade 11</code></li>
                                    <li><strong>A level</strong> — <code>O-Level</code>, <code>A-Level</code>,
                                        <code>Ordinary</code>, <code>Advanced</code></li>
                                    <li><strong>A London board</strong> — <code>London</code>, <code>Edexcel</code>,
                                        <code>Cambridge</code></li>
                                    <li><strong>Languages</strong> — for the foreign-language grade</li>
                                    <li><strong>Skill</strong> — for the skills grade, e.g. <code>Skill Development</code></li>
                                </ul>
                                <p class="naming-guide-ok">
                                    <i class="pi pi-check-circle"></i> Accepted: <code>Grade 10</code>,
                                    <code>Grade 11 / O-Level (Local)</code>, <code>London O-Levels</code>,
                                    <code>Cambridge A-Levels</code>, <code>Languages</code>,
                                    <code>Skill Development</code>
                                </p>
                                <p class="naming-guide-bad">
                                    <i class="pi pi-times-circle"></i> Rejected: <code>Junior Certificate</code>,
                                    <code>Diploma Courses</code>, <code>Grade 12</code> — nothing here maps to a rate.
                                    <code>Grade 12</code> needs a rate decision from the team before it can be added.
                                </p>
                                <p class="naming-guide-note">
                                    Renaming an existing grade does not move its classes, tutor pricing or past papers —
                                    those keep the old name. Rename only when you have to.
                                </p>
                            </div>
                        </div>

                        <label for="subjects" class="Label align-self-start">Subjects</label>
                        <div class="flex flex-column gap-1">
                            <InputText id="subjects" v-model="newSubject" placeholder="Type subject and press Enter..."
                                size="small" variant="filled" class="w-full"
                                :class="{ 'p-invalid': formErrors.subjects }" @keydown="onSubjectKeydown"
                                @blur="addSubject" @input="onSubjectInput" />

                            <small class="p-error">{{ formErrors.subjects }}&nbsp;</small>
                            <div v-if="form.subjects.length > 0"
                                class="surface-overlay border-1 surface-border border-round mt-1"
                                style="max-height: 200px; overflow-y: auto;">
                                <ul class="list-none p-0 m-0">
                                    <li v-for="(subject, index) in form.subjects" :key="index"
                                        class="flex align-items-center justify-content-between px-2 py-1 hover:surface-100 border-bottom-1 surface-border">
                                        <span class="text-sm">{{ subject }}</span>
                                        <i class="pi pi-times text-gray-600 hover:text-red-500 cursor-pointer"
                                            @click="removeSubject(index)"></i>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <label for="mediums" class="Label align-self-start">Mediums</label>
                        <div class="flex flex-column gap-1">
                            <MultiSelect id="mediums" v-model="form.mediums" :options="mediumOptions" optionLabel="label"
                                optionValue="value" placeholder="Select mediums" size="small" variant="filled"
                                class="w-full" :class="{ 'p-invalid': formErrors.mediums }" :maxSelectedLabels="6"
                                panelClass="custom-medium-panel" @change="formErrors.mediums = ''" />
                            <small class="p-error">{{ formErrors.mediums }}&nbsp;</small>
                        </div>
                    </div>
                </div>

                <div class="flex justify-content-end gap-4 mt-4 px-4 py-3">
                    <Button :label="editingGrade ? 'Update Grade' : 'Add Grade'" class="accent-button"
                        @click="submitGrade" :loading="isSubmitting" />
                    <Button label="Cancel" class="accent-button" @click="resetForm" />
                </div>
            </Form>

            <div class="mt-6">
                <GradeList :tableData="tableData" :isLoading="isLoading" :rowsPerPage="rowsPerPage"
                    :totalRecords="totalRecords" :currentPage="currentPage" @page-change="onPageChange"
                    @edit-grade="onEditGrade" @delete-grade="onDeleteGrade" @delete-selected="onDeleteSelected" />
            </div>
        </div>

        <Dialog v-model:visible="showDeleteDialog" header="Delete Grade" modal :closable="false" style="width: 400px;">
            <div class="flex flex-column gap-3">
                <p v-if="deletingGrade">Are you sure you want to delete <strong>{{ deletingGrade?.name }}</strong>? This action cannot
                    be undone.
                </p>
                <div v-else>
                    <p v-if="deletingGradeIds.length === tableData.length">
                        Are you sure you want to delete <strong>{{ deletingGradeIds.length }} grades</strong> in this list? This action cannot be undone.
                    </p>
                    <div v-else>
                        <p>Are you sure you want to delete the following grades?</p>
                        <ul class="pl-4 m-0" style="max-height: 150px; overflow-y: auto;">
                            <li v-for="name in getSelectedGradeNames()" :key="name"><strong>{{ name }}</strong></li>
                        </ul>
                        <p class="mt-2">This action cannot be undone.</p>
                    </div>
                </div>
            </div>
            <template #footer>
                <Button label="Cancel" severity="secondary" @click="closeDeleteDialog" />
                <Button label="Delete" severity="danger" @click="confirmDeleteGrade" :loading="isSubmitting" />
            </template>
        </Dialog>
    </AdminLayout>
</template>

<script setup>
import { Form } from "@primevue/forms";
import { ref, computed, onMounted, watch } from "vue";
import AdminLayout from "@/layout/AdminLayout.vue";
import GradeList from "@/components/dashboard/admin/gradeManagement/gradeList.vue";
import Breadcrumb from "@/components/CustomBreadcrumb.vue";
import Button from "primevue/button";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import Dialog from "primevue/dialog";
import { useToast } from "primevue/usetoast";
import { getGrades, createGrade, updateGrade, deleteGrade, exportGrades } from "@/api/admin";

const toast = useToast();

const tableData = ref([]);
const isLoading = ref(false);
const isSubmitting = ref(false);
const totalRecords = ref(0);
const rowsPerPage = ref(10);
const currentPage = ref(1);
const searchValue = ref("");
const editingGrade = ref(null);
const deletingGrade = ref(null);
const deletingGradeIds = ref([]);
const showDeleteDialog = ref(false);
const newSubject = ref("");
const suggestedGrades = ref([]);
let gradeSearchTimeout = null;

const breadcrumbItems = computed(() => [{ label: "Grade Management" }]);

const showNamingGuide = ref(false);

/**
 * Mirrors getRateForGrade() in the API (src/constants/commissionTiers.js).
 */
const nameResolvesToTier = (raw) => {
    const g = (raw || "")
        .toLowerCase()
        .replace(/[‐-―]/g, "-")
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    if (!g) return true; // nothing typed yet — "required" is a separate error

    const word = (...words) => words.some((w) => new RegExp(`\\b${w}\\b`).test(g));
    const isLondon = word("london", "edexcel", "cambridge");
    const isOLevel = /\bo\s*levels?\b/.test(g) || word("ol", "o l", "ordinary");
    const isALevel = /\ba\s*levels?\b/.test(g) || word("al", "a l", "advanced");

    const match = g.match(/^grade\s*0?(\d{1,2})\b/);
    const number = match ? parseInt(match[1], 10) : null;

    if (isLondon) return isOLevel || isALevel || number !== null;
    if (number !== null && number <= 11) return true;
    return isOLevel || isALevel || word("language", "languages", "scholarship", "skill", "skills");
};

const form = ref({
    name: "",
    subjects: [],
    mediums: []
});

const formErrors = ref({
    name: '',
    subjects: '',
    mediums: ''
});

const gradeNameWarning = computed(() => {
    const name = form.value.name?.trim();
    if (!name || nameResolvesToTier(name)) return "";
    return `"${name}" doesn't show which level the grade is, so it will be rejected. See "How to name a grade".`;
});

// Medium options remain the same
const mediumOptions = [
    { label: "Sinhala", value: "Sinhala" },
    { label: "Tamil", value: "Tamil" },
    { label: "English", value: "English" },
    { label: "Local", value: "Local" },
    { label: "Edexcel", value: "Edexcel" },
    { label: "Cambridge", value: "Cambridge" },
];

const fetchGrades = async () => {
    try {
        isLoading.value = true;
        const res = await getGrades({
            page: currentPage.value,
            limit: rowsPerPage.value,
            searchTerm: searchValue.value,
        });
        tableData.value = res.data.data.grades;
        totalRecords.value = res.data.data.pagination.totalRecords;
    } catch (err) {
        console.error("Failed to fetch grades:", err);
    } finally {
        isLoading.value = false;
    }
};

watch(searchValue, () => {
    currentPage.value = 1;
    fetchGrades();
});

onMounted(() => {
    fetchGrades();
});

const onPageChange = ({ page, limit }) => {
    currentPage.value = page;
    rowsPerPage.value = limit;
    fetchGrades();
};

// Handle grade name input
const onGradeNameInput = () => {
    form.value.name = form.value.name.charAt(0).toUpperCase() + form.value.name.slice(1);
    clearTimeout(gradeSearchTimeout);
    formErrors.value.name = "";

    const query = form.value.name.trim();
    if (!query) {
        suggestedGrades.value = [];
        return;
    }

    // Search for grades after a short delay
    gradeSearchTimeout = setTimeout(async () => {
        try {
            const res = await getGrades({ searchTerm: query, limit: 10, page: 1 });
            const matches = res.data.data.grades;

            const exactMatch = matches.find(g => g.name.toLowerCase() === query.toLowerCase());

            if (editingGrade.value) {
                suggestedGrades.value = [];
                if (exactMatch && exactMatch._id !== editingGrade.value._id) {
                    formErrors.value.name = "Another grade with this name already exists.";
                }
            } else {
                suggestedGrades.value = matches;
                if (exactMatch) {
                    formErrors.value.name = "Grade already exists. Select it from the dropdown to edit.";
                }
            }
        } catch (err) {
            console.error(err);
        }
    }, 300);
};

const hideSuggestions = () => {
    suggestedGrades.value = [];
};

const selectSuggestedGrade = (grade) => {
    onEditGrade(grade); 
    suggestedGrades.value = [];
    formErrors.value.name = "";
};

const onSubjectInput = () => {
    newSubject.value = newSubject.value.charAt(0).toUpperCase() + newSubject.value.slice(1);
};

// Handle new subject input
watch(newSubject, (newVal) => {
    if (!newVal) {
        if (formErrors.value.subjects === "This subject is already added.") {
            formErrors.value.subjects = "";
        }
        return;
    }

    const items = newVal.split(',');
    const currentTyped = items[items.length - 1].trim().toLowerCase();

    if (!currentTyped) return;

    const exists = form.value.subjects.some(sub => sub.toLowerCase() === currentTyped);

    if (exists) {
        formErrors.value.subjects = "This subject is already added.";
    } else {
        formErrors.value.subjects = "";
    }
});

const addSubject = () => {
    if (!newSubject.value) return;
    const subjectsToAdd = newSubject.value.split(',').map(s => s.trim()).filter(s => s);

    subjectsToAdd.forEach(s => {
        const exists = form.value.subjects.some(existing => existing.toLowerCase() === s.toLowerCase());
        if (!exists) {
            form.value.subjects.push(s);
        }
    });

    newSubject.value = "";
    formErrors.value.subjects = "";
};


// Validation logic
const validateForm = () => {
    let isValid = true;

    if (!form.value.name?.trim()) {
        formErrors.value.name = "Grade name is required.";
        isValid = false;
    } else if (formErrors.value.name) {
        isValid = false;
    }

    if (!form.value.subjects?.length) {
        formErrors.value.subjects = "Please select at least one subject.";
        isValid = false;
    }

    if (!form.value.mediums?.length) {
        formErrors.value.mediums = "Please select at least one medium.";
        isValid = false;
    }

    return isValid;
};


const removeSubject = (index) => {
    form.value.subjects.splice(index, 1);
};

const onSubjectKeydown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault();
        addSubject();
    }
};


const submitGrade = async () => {
    if (!validateForm()) return;
    try {
        isSubmitting.value = true;

        if (editingGrade.value) {
            const originalSubjects = editingGrade.value.subjects || [];
            const currentSubjectNames = form.value.subjects;

            const originalNames = originalSubjects.map(sub => sub.name);
            const subjectsToAdd = currentSubjectNames.filter(name => !originalNames.includes(name));

            const subjectsToRemove = originalSubjects
                .filter(sub => !currentSubjectNames.includes(sub.name))
                .map(sub => sub.code);

            const updatePayload = {
                name: form.value.name.trim(),
                mediums: form.value.mediums, // Passing array of mediums
                subjectsToAdd,
                subjectsToRemove
            };

            await updateGrade(editingGrade.value._id, updatePayload);
            toast.add({ severity: "success", summary: "Updated", detail: "Grade updated successfully.", life: 3000 });
        } else {
            const createPayload = {
                name: form.value.name.trim(),
                mediums: form.value.mediums, // Passing array of mediums
                subjects: form.value.subjects,
            };

            await createGrade(createPayload);
            toast.add({ severity: "success", summary: "Created", detail: "Grade added successfully.", life: 3000 });
        }

        resetForm();
        fetchGrades();
    } catch (err) {
        // The API returns its message on `message` (responseHandler.js) — reading `error` here
        // meant every backend rejection surfaced as "Something went wrong."
        const payload = err.response?.data;
        const msg = payload?.message || err.message || "Something went wrong.";
        // The API tags field-level rejections with { field: "name" }, so we bind them to the
        // input rather than pattern-matching the message text.
        const isNameRejection = payload?.data?.field === "name";

        if (isNameRejection) {
            // Too long and too instructional for a toast that disappears — pin it to the field
            // and open the guide so the rule is on screen next to the input being corrected.
            formErrors.value.name = msg;
            showNamingGuide.value = true;
            toast.add({
                severity: "warn",
                summary: "Grade name rejected",
                detail: "Check the message under the Grade name field.",
                life: 4000,
            });
            window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            toast.add({ severity: "error", summary: "Error", detail: msg, life: 6000 });
        }
    } finally {
        isSubmitting.value = false;
    }
};

const onEditGrade = (grade) => {
    editingGrade.value = grade;
    form.value = {
        name: grade.name,
        subjects: grade.subjects.map((s) => s.name),
        // Map backend objects [{ name: 'Sinhala', code: 'sinhala' }] to array of strings ['Sinhala'] for the MultiSelect
        mediums: grade.mediums ? grade.mediums.map(m => m.name) : [], 
    };
    formErrors.value = {};
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const getSelectedGradeNames = () => {
    return tableData.value
        .filter(g => deletingGradeIds.value.includes(g._id))
        .map(g => g.name);
};

const onDeleteGrade = (grade) => { deletingGrade.value = grade; showDeleteDialog.value = true; };
const onDeleteSelected = (ids) => { deletingGradeIds.value = ids; showDeleteDialog.value = true; };
const closeDeleteDialog = () => { showDeleteDialog.value = false; deletingGrade.value = null; deletingGradeIds.value = []; };

const confirmDeleteGrade = async () => {
    try {
        isSubmitting.value = true;
        let ids = [];
        if (deletingGrade.value) {
            ids = [deletingGrade.value._id];
        } else if (deletingGradeIds.value.length > 0) {
            ids = deletingGradeIds.value;
        }
        await deleteGrade({ ids });
        toast.add({ severity: "success", summary: "Deleted", detail: "Grade removed.", life: 3000 });
        fetchGrades();
    } catch {
        toast.add({ severity: "error", summary: "Error", detail: "Failed to delete grade.", life: 4000 });
    } finally {
        isSubmitting.value = false;
        closeDeleteDialog();
    }
};

const resetForm = () => {
    form.value = { name: "", subjects: [], mediums: [] };
    formErrors.value = { name: '', subjects: '', mediums: '' };
    newSubject.value = "";
    editingGrade.value = null;
    suggestedGrades.value = []; 
};

const handleExport = async () => {
    try {
        const res = await exportGrades();
        const link = document.createElement("a");
        link.href = res.data.data.downloadLink;
        link.download = "grade_management.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.add({ severity: "success", summary: "Exported", detail: "File downloaded.", life: 3000 });
    } catch (err) {
        console.error("Export failed:", err);
    }
};
</script>

<style scoped>
.p-error {
    color: red;
    font-size: 10px;
}

.p-error,
.p-warn {
    font-size: 11px;
    line-height: 1.5;
}

.p-warn {
    color: #b45309;
}

.naming-guide-toggle {
    font-size: 11px;
    color: var(--accent-500, #6366f1);
    cursor: pointer;
    user-select: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.naming-guide-toggle:hover {
    text-decoration: underline;
}

.naming-guide-toggle i {
    font-size: 11px;
}

.naming-guide {
    margin-top: 6px;
    padding: 10px 12px;
    border: 1px solid var(--surface-200, #e5e7eb);
    border-left: 3px solid var(--accent-500, #6366f1);
    border-radius: 4px;
    background: var(--surface-50, #f9fafb);
    font-size: 11px;
    line-height: 1.6;
    color: var(--gray-700, #374151);
}

.naming-guide p {
    margin: 0 0 6px;
}

.naming-guide p:last-child {
    margin-bottom: 0;
}

.naming-guide-list {
    margin: 0 0 6px;
    padding-left: 18px;
}

.naming-guide code {
    background: var(--surface-200, #e5e7eb);
    border-radius: 3px;
    padding: 0 4px;
    font-size: 10.5px;
    white-space: nowrap;
}

.naming-guide-ok {
    color: #047857;
}

.naming-guide-bad {
    color: #b45309;
}

.naming-guide-note {
    padding-top: 6px;
    border-top: 1px dashed var(--surface-300, #d1d5db);
    color: var(--gray-600, #4b5563);
}

.form {
    width: 100%;
    background-color: white;
    border-radius: 5px;
}

.container {
    width: 100%;
    height: auto;
}

@media (max-width: 768px) {
    .form {
        min-width: none;
    }
}
</style>

<style>
/* Custom styles for the MultiSelect */
.custom-medium-panel .p-checkbox {
    --p-checkbox-checked-background: var(--accent-500);
    --p-checkbox-checked-hover-background: var(--accent-500);
    --p-checkbox-border-color: var(--accent-500);
    --p-checkbox-checked-border-color: var(--accent-500);
    --p-checkbox-checked-hover-border-color: var(--accent-500);
}

.custom-medium-panel .p-multiselect-option {
    font-size: 14px;
    color: black;
    padding: 0.25rem 0.8rem;
    border-bottom: 1px solid var(--gray-300);
    border-radius: 0;
}

.custom-medium-panel .p-multiselect-option:last-child {
    border-bottom: none;
}

.custom-medium-panel .p-multiselect-option[data-p-highlight="false"]:not(.p-disabled):hover {
    background-color: #f4f4f5;
}
</style>